import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, KeyRound, ShieldCheck } from 'lucide-react';
import { encode } from 'uqr';
import type { TwoFactorSetup } from '@iptv/shared-types';
import { Alert, Modal, useToast } from '../components/feedback.js';
import { CopyButton } from '../components/data.js';
import { Badge, Button, Card, Field, Input, Spinner } from '../components/primitives.js';
import { useAuth } from './auth.js';
import { useErrorText } from './errors.js';

/**
 * QR code drawn as one SVG path. It stays dark on white in both themes: authenticator cameras
 * read inverted codes poorly.
 */
export function QrCode({ value, label }: { value: string; label: string }) {
  const { path, size } = useMemo(() => {
    const qr = encode(value, { ecc: 'M', border: 2 });
    let d = '';
    qr.data.forEach((row, y) =>
      row.forEach((dark, x) => {
        if (dark) d += `M${x} ${y}h1v1h-1z`;
      }),
    );
    return { path: d, size: qr.size };
  }, [value]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      className="size-44 rounded-lg bg-white shadow-[inset_0_0_0_1px_var(--ui-line)]"
    >
      <path d={path} fill="#000" />
    </svg>
  );
}

/** One field for the 6-digit code or a recovery code. */
export function CodeField({
  value,
  onChange,
  error,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Field label={t('twoFactor.code')} hint={t('twoFactor.codeHint')} error={error ?? undefined}>
      {(p) => (
        <Input
          {...p}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="text"
          autoComplete="one-time-code"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={12}
          dir="ltr"
          autoFocus={autoFocus}
          className="font-mono tracking-[0.2em]"
        />
      )}
    </Field>
  );
}

/** Step 1 of turning 2FA on: add the account to an authenticator app. */
export function AuthenticatorSetup({ setup }: { setup: TwoFactorSetup }) {
  const { t } = useTranslation();
  const grouped = setup.secret.match(/.{1,4}/g)!.join(' ');
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">{t('twoFactor.setupIntro')}</p>
      {/* On a phone the QR code is on the same screen as the camera, so the link comes first. */}
      <Button
        variant="secondary"
        className="w-full sm:hidden"
        icon={<ExternalLink className="size-4" aria-hidden />}
        onClick={() => window.location.assign(setup.otpauthUri)}
      >
        {t('twoFactor.openApp')}
      </Button>
      <div className="hidden justify-center sm:flex">
        <QrCode value={setup.otpauthUri} label={t('twoFactor.qrLabel')} />
      </div>
      <div className="rounded-lg bg-[var(--control-bg)] p-3.5">
        <div className="text-xs text-muted">{t('twoFactor.manualKey')}</div>
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
          <code className="ltr-iso font-mono text-sm font-semibold break-all select-all">
            {grouped}
          </code>
          <CopyButton value={setup.secret} />
        </div>
      </div>
    </div>
  );
}

/** Shown once: the codes that sign in when the phone is lost. */
export function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const { t } = useTranslation();
  const text = codes.join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: 'recovery-codes.txt',
    });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <Alert tone="warning">{t('twoFactor.recoveryWarning')}</Alert>
      <ul className="ltr-iso grid grid-cols-2 gap-2" aria-label={t('twoFactor.recoveryCodes')}>
        {codes.map((c) => (
          <li
            key={c}
            className="rounded-control bg-[var(--control-bg)] py-2 text-center font-mono text-sm font-semibold tabular-nums select-all"
          >
            {c}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <CopyButton value={text} label={t('twoFactor.copyAll')} />
        <Button
          variant="secondary"
          size="sm"
          icon={<Download className="size-4" aria-hidden />}
          onClick={download}
        >
          {t('twoFactor.download')}
        </Button>
      </div>
    </div>
  );
}

type Dialog = 'enable' | 'disable' | 'codes' | null;

/** Account page card: turn two-step verification on or off and replace recovery codes. */
export function TwoFactorCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dialog, setDialog] = useState<Dialog>(null);
  if (!user) return null;
  const on = user.twoFactorEnabled;
  return (
    <Card
      title={t('twoFactor.title')}
      description={t('twoFactor.description')}
      actions={
        <Badge tone={on ? 'success' : 'neutral'}>{t(on ? 'twoFactor.on' : 'twoFactor.off')}</Badge>
      }
    >
      {user.twoFactorRequired && (
        <p className="mb-4 text-sm text-muted">{t('twoFactor.requiredNote')}</p>
      )}
      <div className="flex flex-wrap gap-2 max-sm:*:w-full">
        {on ? (
          <>
            <Button
              variant="secondary"
              icon={<KeyRound className="size-4" aria-hidden />}
              onClick={() => setDialog('codes')}
            >
              {t('twoFactor.newCodes')}
            </Button>
            {!user.twoFactorRequired && (
              <Button variant="ghost" onClick={() => setDialog('disable')}>
                {t('twoFactor.turnOff')}
              </Button>
            )}
          </>
        ) : (
          <Button
            icon={<ShieldCheck className="size-4" aria-hidden />}
            onClick={() => setDialog('enable')}
          >
            {t('twoFactor.turnOn')}
          </Button>
        )}
      </div>
      {dialog === 'enable' && <EnableDialog onClose={() => setDialog(null)} />}
      {dialog === 'disable' && <DisableDialog onClose={() => setDialog(null)} />}
      {dialog === 'codes' && <NewCodesDialog onClose={() => setDialog(null)} />}
    </Card>
  );
}

function EnableDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useAuth();
  const errorText = useErrorText();
  const { refreshUser } = useAuth();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  // One secret per opening of the dialog: never refetched (a new secret would not match the app),
  // and dropped when the dialog closes.
  const setup = useQuery({
    queryKey: ['two-factor-setup'],
    queryFn: () => api.twoFactor.setup(),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const enable = useMutation({
    mutationFn: () => api.twoFactor.enable(code),
    onSuccess: (r) => {
      setCodes(r.recoveryCodes);
      void refreshUser();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t(codes ? 'twoFactor.recoveryCodes' : 'twoFactor.turnOn')}
      footer={
        codes ? (
          <Button onClick={onClose}>{t('twoFactor.savedCodes')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={!setup.data || code.trim().length < 6}
              loading={enable.isPending}
              onClick={() => enable.mutate()}
            >
              {t('twoFactor.turnOn')}
            </Button>
          </>
        )
      }
    >
      {codes ? (
        <RecoveryCodesPanel codes={codes} />
      ) : setup.isError ? (
        <Alert tone="danger">{errorText(setup.error)}</Alert>
      ) : !setup.data ? (
        <Spinner className="h-48" />
      ) : (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim().length >= 6) enable.mutate();
          }}
        >
          <AuthenticatorSetup setup={setup.data} />
          <CodeField
            value={code}
            onChange={setCode}
            error={enable.isError ? errorText(enable.error) : null}
          />
        </form>
      )}
    </Modal>
  );
}

function DisableDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useAuth();
  const toast = useToast();
  const errorText = useErrorText();
  const { refreshUser } = useAuth();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const disable = useMutation({
    mutationFn: () => api.twoFactor.disable(password, code),
    onSuccess: () => {
      toast.success(t('twoFactor.turnedOff'));
      void refreshUser();
      onClose();
    },
  });
  return (
    <CodeDialog
      title={t('twoFactor.turnOff')}
      intro={t('twoFactor.turnOffIntro')}
      submitLabel={t('twoFactor.turnOff')}
      danger
      onClose={onClose}
      onSubmit={() => disable.mutate()}
      pending={disable.isPending}
      ready={password.length > 0 && code.trim().length >= 6}
      error={disable.isError ? errorText(disable.error) : null}
    >
      <Field label={t('auth.password')}>
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="current-password"
            dir="ltr"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <CodeField value={code} onChange={setCode} />
    </CodeDialog>
  );
}

function NewCodesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useAuth();
  const errorText = useErrorText();
  const [code, setCode] = useState('');
  const regenerate = useMutation({ mutationFn: () => api.twoFactor.recoveryCodes(code) });
  if (regenerate.data)
    return (
      <Modal
        open
        onClose={onClose}
        title={t('twoFactor.recoveryCodes')}
        footer={<Button onClick={onClose}>{t('twoFactor.savedCodes')}</Button>}
      >
        <RecoveryCodesPanel codes={regenerate.data.recoveryCodes} />
      </Modal>
    );
  return (
    <CodeDialog
      title={t('twoFactor.newCodes')}
      intro={t('twoFactor.newCodesIntro')}
      submitLabel={t('twoFactor.newCodes')}
      onClose={onClose}
      onSubmit={() => regenerate.mutate()}
      pending={regenerate.isPending}
      ready={code.trim().length >= 6}
      error={regenerate.isError ? errorText(regenerate.error) : null}
    >
      <CodeField value={code} onChange={setCode} autoFocus />
    </CodeDialog>
  );
}

function CodeDialog({
  title,
  intro,
  submitLabel,
  danger,
  onClose,
  onSubmit,
  pending,
  ready,
  error,
  children,
}: {
  title: string;
  intro: string;
  submitLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  pending: boolean;
  ready: boolean;
  error: string | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            disabled={!ready}
            loading={pending}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onSubmit();
        }}
      >
        <p className="text-sm text-muted">{intro}</p>
        {error && <Alert tone="danger">{error}</Alert>}
        {children}
      </form>
    </Modal>
  );
}
